/* eslint-disable prefer-rest-params */

import type { AxiosError } from 'axios';
import Axios from 'axios';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { appEnv, authApiHost, isLocalDevelopment } from '../app-config';
import { getQueryObj, preserveWindowQuery } from './uri';

const sent: string[] = [];

// Sliding-window rate limiter: prevent error/render loops from flooding the server.
// Silent drop — never log here (console.error is intercepted by this module).
const WINDOW_MS = 10_000;
const MAX_POSTS_PER_WINDOW = 30;
const recentSendTimestamps: number[] = [];

// A single searchable string built from the fields an error can arrive in:
// `name`/`message` (window.onerror & unhandledrejection paths) and `arguments`
// (intercepted console.error). Used by the substring-based suppression rules below.
function combinedText(data: ISubmitData): string {
  const message = typeof data.message === 'string' ? data.message : '';
  const args = Array.isArray(data.arguments) ? data.arguments.join(' ') : '';
  return `${data.name ?? ''} ${message} ${args}`;
}

// Errors thrown by browser extensions / injected third-party scripts, never by our
// own code. None of these identifiers or APIs are used anywhere in the app
// (verified: no first-party `.addListener(`, `chrome.*`, `gtmObject`, MetaMask, or
// `requestIdleCallback` usage), so any error mentioning them is injected content.
const THIRD_PARTY_NOISE_SIGNATURES: readonly string[] = [
  'addListener', // extension content scripts: `chrome.runtime.onMessage.addListener` on an undefined API
  'chrome.storage', // extension storage access
  'runtime.sendMessage', // extension messaging (e.g. "Invalid call to runtime.sendMessage(). Tab not found.")
  'gtmObject', // Google Tag Manager container noise
  "COOKIEBOT_VERSION' has already been declared", // cookie-consent script double-injection
  "ONETRUST_VERSION' has already been declared", // cookie-consent script double-injection
  'MetaMask', // crypto-wallet extension
  'requestIdleCallback', // absent in some in-app/extension webviews
];

// Expected/benign or transient messages that are not actionable app bugs: a
// user-facing validation message, request timeouts, and an unpinnable circular-
// structure serialization thrown by third-party/analytics code (tracked for a
// sourcemap root-cause in docs/TODO.md).
const BENIGN_NOISE_SIGNATURES: readonly string[] = [
  "We've already sent an email to your address recently", // email-resend throttle (user-facing validation)
  'timeout exceeded', // axios/network request timeout
  'Converting circular structure to JSON', // circular-ref serialization (third-party/analytics; unpinned)
];

export function isUseless(data: ISubmitData, dataString: string): boolean {
  if (!data) {
    return true;
  }
  if (data.colno === 0 && data.lineno === 0 && !data.source && data.message === 'Script error.') {
    return true;
  }
  if (Array.isArray(data.arguments) && data.arguments[0] === 'AxiosError: Request aborted') {
    return true;
  }
  if (data.axiosErr && (data.axiosErr.code === 'ERR_NETWORK' || data.axiosErr.code === 'ECONNABORTED')) {
    return true;
  }
  if (isEqual(omit(data, ['href']), { arguments: ['AxiosError: Network Error'] })) {
    return true;
  }
  // Suppress AxiosError network noise from the unhandledrejection path (no axiosErr object there)
  if (
    data.name === 'AxiosError' &&
    typeof data.message === 'string' &&
    (data.message === 'Network Error' ||
      data.message === 'Request aborted' ||
      data.message.startsWith('Network Error') ||
      data.message.startsWith('Request aborted'))
  ) {
    return true;
  }
  // Expected session-expiry. Only suppress a 401 that ApiCall actually handled as
  // session-expiry (cleared the token + redirected to login), which it marks with
  // `isSessionExpiry` (src/store/api/api-call.ts). A 401 on a request that opted out of
  // that treatment — a `doNotRemoveAuthCookie` OAuth connect/disconnect, or a 401 raised
  // inside the /login & /join flows — is NOT flagged and still logs, so genuine auth
  // bugs there stay visible.
  if (data.isSessionExpiry === true) {
    return true;
  }
  // Benign "…→ skipped" business-rule rejections from the fire-and-forget skip action:
  // the backend rejects a terminal-state→skipped transition, surfaced as an unhandled
  // ClientError rejection. Expected, not app bugs. Scoped to the known terminal states
  // so an unexpected non-terminal rejection (e.g. started→skipped) still logs.
  if (
    data.name === 'ClientError' &&
    typeof data.message === 'string' &&
    /^Can't change participation status from (completed|disqualified|quota_full|screened) to skipped!$/.test(
      data.message
    )
  ) {
    return true;
  }
  // Parse-time SyntaxErrors never come from our own compiled bundle (it is valid JS
  // by build time; a corrupt/half-loaded chunk surfaces as a dynamic-import failure,
  // handled by isReload). A SyntaxError whose `source` is the current document URL is an inline
  // <script> injected verbatim from admin/campaign-configured tracking-pixel HTML
  // (src/utils/tracking-tags.ts, or thank_you.tracking_pixel) — a config bug in that
  // third-party snippet, not an app bug. We ship no first-party inline scripts, so a
  // document-sourced SyntaxError is always injected third-party content. Errors from
  // real script resources (our .js/.mjs bundle, external tracking .js) keep a distinct
  // `source`, and no-`source`/empty-`source` rejections (e.g. JSON.parse via
  // unhandledrejection) never equal the page URL — all preserved.
  if (
    data.name === 'SyntaxError' &&
    typeof data.source === 'string' &&
    data.source.split('?')[0].split('#')[0] === data.href.split('?')[0].split('#')[0]
  ) {
    return true;
  }
  // Browser-extension / injected-third-party-script noise (see signatures above).
  const text = combinedText(data);
  if (THIRD_PARTY_NOISE_SIGNATURES.some((sig) => text.includes(sig))) {
    return true;
  }
  // Expected/benign or transient noise (see signatures above).
  if (BENIGN_NOISE_SIGNATURES.some((sig) => text.includes(sig))) {
    return true;
  }
  return false;
}

// Signatures of a failed dynamic import / module preload. Under Vite these surface
// as a plain Error/TypeError whose *message* (not name) identifies them — the old
// webpack `ChunkLoadError` name never occurs in this build, so name-only matching
// (the previous implementation) never fired. After a deploy the running document
// holds a stale index.html referencing hashed chunk URLs that no longer exist on the
// server, so the import rejects; these are transient and self-heal on a reload.
const DYNAMIC_IMPORT_ERROR_SIGNATURES: readonly string[] = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk', // e.g. "Loading chunk 559 failed."
  'ChunkLoadError', // legacy/webpack safety net
];

export function isDynamicImportError(text: string | null | undefined): boolean {
  if (typeof text !== 'string' || !text) {
    return false;
  }
  return DYNAMIC_IMPORT_ERROR_SIGNATURES.some((sig) => text.includes(sig));
}

// Guard against a reload loop when the failure persists past the first reload.
// A URL `?refresh` param is NOT a reliable one-shot guard on its own: startup code
// (auth-token.ts) strips it via history.replaceState, which would re-arm the reload
// and loop. The primary guard is therefore a short-lived sessionStorage timestamp
// that survives the reload and cannot be stripped — if a dynamic import is still
// failing within RELOAD_GUARD_MS of the last reload we do NOT reload again (the
// error is then allowed to surface and log), while a genuinely new failure later in
// the session is still recoverable once the window elapses. The `?refresh` param is
// kept as a visible marker and a fallback for when sessionStorage is unavailable.
const RELOAD_TS_KEY = 'pp:dynamic-import-reload-ts';
const RELOAD_GUARD_MS = 15_000;

export function reloadOnce(): boolean {
  const now = Date.now();
  let sessionStorageOk = true;
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
    if (last && now - last < RELOAD_GUARD_MS) {
      return false;
    }
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
  } catch {
    // sessionStorage blocked (private mode / sandboxed iframe): fall back to the
    // URL param guard, accepting that startup may strip it.
    sessionStorageOk = false;
  }
  if (!sessionStorageOk && typeof getQueryObj().refresh !== 'undefined') {
    return false;
  }
  window.location.href = preserveWindowQuery(`${window.location.origin}${window.location.pathname}?refresh=${now}`);
  return true;
}

export function isReload(data: ISubmitData, dataString: string): boolean {
  if (!data) {
    return false;
  }
  if (isDynamicImportError(combinedText(data))) {
    return reloadOnce();
  }
  return false;
}

function isExcluded(data: ISubmitData, dataString: string): boolean {
  if (!data || !dataString) {
    return true;
  }
  if (isEqual(omit(data, 'href'), { colno: 0, lineno: 0, source: '', message: 'Script error.' })) {
    return true;
  }
  if (typeof data.source === 'string' && data.source.startsWith('https://googleads.g.doubleclick.net/')) {
    return true;
  }
  if (typeof data.arguments?.[0] === 'string') {
    if (
      data.arguments[0].startsWith('Asset') &&
      data.arguments[0].includes('got suspended, please visit https://anytrack.io for more details.')
    ) {
      return true;
    }
    if (data.arguments[0].startsWith('Pinterest Tag Error:')) {
      return true;
    }
  }
  return sent.includes(dataString);
}

interface ISubmitData {
  href: string;
  [other: string]: any;
  arguments?: any[];
  axiosErr?: AxiosError;
}

type LogLevel = 'info' | 'warn' | 'error';

// Strip query string and hash from a URL so credential tokens carried in query
// params (e.g. ?token=…, ?pwd-token=…) never reach the remote log store.
function sanitizeUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
}

// Only the page-location keys carry credential tokens in query params. `source`
// is a script URL (no tokens) and may be a bare filename, so it is left as-is.
const URL_ATTRIBUTE_KEYS = new Set(['href', 'url']);

function buildPayload(level: LogLevel, data: ISubmitData) {
  // Derive a single message string. NEVER JSON.stringify the whole `data`: it can
  // carry an AxiosError whose toJSON() embeds request headers (incl. the user's
  // pp-token), which would leak into the log payload.
  let message: string;
  if (typeof data.message === 'string' && data.message) {
    message = data.message;
  } else if (Array.isArray(data.arguments) && data.arguments.length > 0) {
    message = data.arguments.join(' ');
  } else if (data.axiosErr) {
    message = `${data.axiosErr.name ?? 'AxiosError'}: ${data.axiosErr.message}`;
  } else {
    message = 'Unspecified client error';
  }

  // Attributes: primitives only, omit undefined values; URLs are sanitized.
  const attributes: Record<string, string | number | boolean> = {};
  const primitiveKeys = ['href', 'name', 'source', 'stack', 'lineno', 'colno', 'url'] as const;
  for (const key of primitiveKeys) {
    const val = data[key];
    if (
      val !== undefined &&
      val !== null &&
      (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
    ) {
      attributes[key] = typeof val === 'string' && URL_ATTRIBUTE_KEYS.has(key) ? sanitizeUrl(val) : val;
    }
  }
  // Carry axios info if present
  if (data.axiosErr) {
    const { code, status, response } = data.axiosErr;
    if (code) {
      attributes.axiosCode = String(code);
    }
    const resolvedStatus = status ?? response?.status;
    if (resolvedStatus !== undefined) {
      attributes.axiosStatus = Number(resolvedStatus);
    }
  }

  return {
    service: 'pureprofile-app' as const,
    level,
    message,
    ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
  };
}

export function submitData(level: LogLevel, data: ISubmitData) {
  if (isLocalDevelopment) {
    return;
  }
  let dataString: string;
  try {
    dataString = JSON.stringify(data);
  } catch {
    dataString = String(data?.message ?? data?.href ?? 'unserializable');
  }
  if (isUseless(data, dataString)) {
    return;
  }
  if (isReload(data, dataString)) {
    return;
  }
  if (isExcluded(data, dataString)) {
    return;
  }
  // Rate limiter: sliding window over WINDOW_MS; silently drop when at capacity.
  const now = Date.now();
  let i = 0;
  while (i < recentSendTimestamps.length && recentSendTimestamps[i] <= now - WINDOW_MS) {
    i++;
  }
  recentSendTimestamps.splice(0, i);
  if (recentSendTimestamps.length >= MAX_POSTS_PER_WINDOW) {
    return;
  }
  recentSendTimestamps.push(now);
  // Cap dedup array to the most recent 200 entries to bound memory.
  sent.push(dataString);
  if (sent.length > 200) {
    sent.shift();
  }
  void Axios.request({
    method: 'POST',
    url: `${authApiHost}/api/v1/public/client-log`,
    data: buildPayload(level, data),
  }).catch(() => {});
}

// hook to console methods
function handleIntercepted(level: LogLevel, args: IArguments) {
  const data: ISubmitData = {
    href: window.location.href,
    arguments: Array.from(args).map((i) => i?.toString()),
  };
  submitData(level, data);
}

function takeOverConsole() {
  const { console } = window;
  if (!console) {
    return;
  }

  function intercept(level: LogLevel, method: keyof Console) {
    const original = console[method] as any;
    (console as any)[method] = function () {
      try {
        handleIntercepted(level, arguments);
      } catch (err) {
        // ignore
      }
      return original.apply
        ? // do this for normal browsers
          original.apply(console, arguments)
        : // do this for IE
          original(Array.prototype.slice.apply(arguments).join(' '));
    } as any;
  }

  // Only intercept console.error — console.warn is NOT intercepted here to avoid
  // forwarding arbitrary third-party warn noise. Explicit logWarn calls submit directly.
  intercept('error', 'error');
}
takeOverConsole();

// hook to window.onerror
const previousWindowOnError = window.onerror;
window.onerror = function (...args) {
  const [message, source, lineno, colno, error] = args;
  const data: ISubmitData = {
    href: window.location.href,
    name: error?.name,
    message,
    source,
    stack: error?.stack,
    lineno,
    colno,
  };
  submitData('error', data);
  previousWindowOnError?.(...args);
};

// hook to window.unhandledrejection
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const data: ISubmitData = {
    href: window.location.href,
    name: reason?.name,
    message: reason?.message ?? String(reason),
    stack: reason?.stack,
    isSessionExpiry: reason?.isSessionExpiry,
  };
  submitData('error', data);
});

// Vite fires this on the window when a module preload fails (before the import
// rejection propagates). Recover by reloading once; preventDefault stops Vite's
// default behaviour of rethrowing the error. See
// https://vite.dev/guide/build.html#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  // Only swallow the event if we actually reloaded. If reloadOnce() declined (a
  // failure that persists past the one-shot guard), let the error propagate so it
  // still reaches telemetry instead of being silenced by preventDefault().
  if (reloadOnce()) {
    event.preventDefault();
  }
});

// export logging utilities
// logError routes through console.error, which is intercepted by takeOverConsole() — no double-submit.
// logWarn calls submitData directly (console.warn is NOT intercepted) and also calls console.warn.
export const log = {
  info: (msg: string) => console.log(msg),
  warn: (msg: string) => {
    submitData('warn', { href: window.location.href, message: msg });
    console.warn(msg);
  },
  error: (msg: string) => console.error(msg),
  debug: (...args: any[]) => {
    if (appEnv === 'development') {
      console.debug(...args);
    }
  },
  trace: (msg: string) => console.trace(msg),
};
export const logInfo = log.info;
export const logWarn = log.warn;
export const logError = log.error;
export const logTrace = log.trace;
